import { useState, useCallback, useEffect } from 'react';
import { CourierPackageApi, InvoiceApi } from '@shared/sdk-v1/apis';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';

interface PackageSummary {
  readyForPickup: number;
  totalOutstanding: number;
  isLoading: boolean;
}

export const usePackageSummary = (): PackageSummary => {
  const { user, courier } = useAuth();
  const [readyForPickup, setReadyForPickup] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSummary = useCallback(async () => {
    if (!user?.id || !courier?.id) return;

    setIsLoading(true);

    const packageApi = new CourierPackageApi(apiConfig());
    const invoiceApi = new InvoiceApi(apiConfig());

    const [readyResult, balanceResult] = await Promise.allSettled([
      packageApi.courierPackageCount({
        where: JSON.stringify({
          ownerId: user.id,
          courierId: courier.id,
          status: 'Ready',
        }),
      }),
      invoiceApi.invoiceGetCustomerBalance({
        invoiceGetCustomerBalanceRequest: { customerId: user.id as number },
      }),
    ]);

    if (readyResult.status === 'fulfilled') {
      setReadyForPickup(readyResult.value.count || 0);
    }
    if (balanceResult.status === 'fulfilled') {
      const balance = balanceResult.value as { totalOutstanding?: string };
      setTotalOutstanding(parseFloat(balance.totalOutstanding || '0'));
    }

    setIsLoading(false);
  }, [user?.id, courier?.id]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { readyForPickup, totalOutstanding, isLoading };
};
